import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useRealtimeRefresh from '../../realtime/useRealtimeRefresh';
import { fetchRequest as getDeploymentDetail, clearState } from '../../redux/actions/deployments/detail';
import {
  fetchRequest as inferenceCheckRequest,
  clearState as inferenceCheckClearState,
} from '../../redux/actions/deployments/inferenceCheck';
import { deleteRequest as deleteDeployment } from '../../redux/actions/deployments/delete';
import { fetchUsageRequest as getDeploymentUsage, clearUsageState } from '../../redux/actions/deployments/usage';
import { segmentInferenceRequest as runSegmentInference, clearInferenceState, segmentInferenceFailure } from '../../redux/actions/deployments/inference';
import { retryRequest, clearState as clearRetryState } from '../../redux/actions/promotion/retry';
import { fetchRequest as getSegmentDetail, clearState as clearSegmentState } from '../../redux/actions/segments/detail';
import { fetchRequest as getWorkspaceSegments } from '../../redux/actions/segments/list';
import { getModelDetail as fetchModelDetail } from '../../redux/actions/models/detail';
import { 
  Box, Typography, Paper, Grid, CircularProgress, Divider, 
  Chip, IconButton, Stack, Drawer,
  Tooltip, Link, Alert, Dialog, DialogTitle, DialogContent, 
  DialogContentText, DialogActions, Table, TableBody, TableCell, TableRow,
  Tabs, Tab, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { DeploymentsIcon, SegmentsIcon } from '../global/CustomIcons';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import LaunchIcon from '@mui/icons-material/Launch';
import CodeIcon from '@mui/icons-material/Code';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme, alpha } from '@mui/material/styles';
import { format } from '../../utils/datetime';
import { ResponsiveLine } from '@nivo/line';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import StatusChip from '../common/StatusChip';
import TrainingDetailsModal from '../models/TrainingDetailsModal';
import { accentFor } from '../common/ForecastCharts';
import { Surface } from '../dashboard/components/DashboardPrimitives';
import { CMButton, CMButtonOutlined, CMButtonError } from '../../containers/styledComponents';
import config from '../../config';
import { useDisplayNames } from '../../utils/displayNames';
import { useSignalUnits } from '../../utils/units';

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`deployment-tabpanel-${index}`}
      aria-labelledby={`deployment-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// ── API guide primitives ──────────────────────────────────────────────────
// A self-contained code surface (terminal-window styling) with a header
// label and a one-click copy that confirms inline. Theme-aware: GitHub-dark
// canvas in dark mode, GitHub-light canvas in light mode (matches
// auth/CodeSurface.js).
const CODE_PALETTES = {
  dark: {
    bg: '#0d1117',
    headerBg: '#161b22',
    border: '#30363d',
    text: '#d7dce3',
    muted: '#8b949e',
    copyHover: '#fff',
  },
  light: {
    bg: '#f6f8fa',
    headerBg: '#eaeef2',
    border: '#d0d7de',
    text: '#1f2328',
    muted: '#57606a',
    copyHover: '#1f2328',
  },
};
const CODE_DOTS = ['#ff5f56', '#ffbd2e', '#27c93f'];
const API_LANGS = [
  { id: 'curl', label: 'cURL' },
  { id: 'python', label: 'Python' },
  { id: 'javascript', label: 'JavaScript' },
];

// Lightweight, language-agnostic syntax colouring for the code surfaces.
// We colour double-quoted strings, numbers, booleans and a small set of
// keywords — enough to make params pop without a real tokenizer (robust
// because every string in our snippets is single-line). No comment rule on
// purpose: `//` in URLs must not be mistaken for a comment.
//
// When `fields` + `onFieldClick` are supplied, any token that names a known
// request field (a bare key like `input`, or a quoted header like
// `"X-Workspace-ID"`) renders as a clickable, dotted-underlined link that
// opens its detail drawer — so the code itself is the reference.
const HL_PALETTES = {
  // GitHub-dark token colors.
  dark: {
    string: '#7ee787',
    keyword: '#ff7b72',
    boolean: '#ffa657',
    number: '#79c0ff',
    field: '#d2a8ff',
  },
  // GitHub-light token colors.
  light: {
    string: '#0a3069',
    keyword: '#cf222e',
    boolean: '#953800',
    number: '#0550ae',
    field: '#8250df',
  },
};
const HL_BASE = '("(?:\\\\.|[^"\\\\])*")|\\b(const|let|await|async|return|import|from|print|def)\\b|\\b(true|false|null|True|False|None)\\b|(\\b\\d+(?:\\.\\d+)?\\b)';
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const fieldLink = (key, text, name, onFieldClick, hl) => (
  <span
    key={key}
    onClick={(e) => { e.stopPropagation(); onFieldClick(name); }}
    style={{
      color: hl.field,
      cursor: 'pointer',
      textDecoration: 'underline dotted',
      textUnderlineOffset: 3,
    }}
  >
    {text}
  </span>
);

const highlightCode = (code, fields, onFieldClick, hl) => {
  const fieldNames = (fields || []).map((f) => f.name);
  const hasFields = fieldNames.length > 0 && typeof onFieldClick === 'function';
  const fieldSet = new Set(fieldNames);
  const pattern = hasFields ? `(${fieldNames.map(escapeRe).join('|')})|${HL_BASE}` : HL_BASE;
  const re = new RegExp(pattern, 'g');
  const off = hasFields ? 1 : 0;
  const nodes = [];
  let last = 0;
  let key = 0;
  let m = re.exec(code);
  while (m !== null) {
    if (m.index > last) nodes.push(code.slice(last, m.index));
    const text = m[0];
    if (hasFields && m[1]) {
      // Bare field name (e.g. unquoted JS object key, or a curl header name).
      nodes.push(fieldLink(key, text, text, onFieldClick, hl));
    } else {
      const str = m[off + 1];
      const kw = m[off + 2];
      const bool = m[off + 3];
      if (str) {
        const inner = text.slice(1, -1);
        if (hasFields && fieldSet.has(inner)) {
          nodes.push(fieldLink(key, text, inner, onFieldClick, hl));
        } else {
          nodes.push(<span key={key} style={{ color: hl.string }}>{text}</span>);
        }
      } else if (kw) {
        nodes.push(<span key={key} style={{ color: hl.keyword }}>{text}</span>);
      } else if (bool) {
        nodes.push(<span key={key} style={{ color: hl.boolean }}>{text}</span>);
      } else {
        nodes.push(<span key={key} style={{ color: hl.number }}>{text}</span>);
      }
    }
    key += 1;
    last = m.index + text.length;
    if (re.lastIndex === m.index) re.lastIndex += 1;
    m = re.exec(code);
  }
  if (last < code.length) nodes.push(code.slice(last));
  return nodes;
};

const CopyChip = ({ text, title = 'Copy', palette }) => {
  const theme = useTheme();
  // Callers inside a CodeBlock pass its code-surface palette; standalone
  // usages (e.g. the endpoint bar) fall back to the theme-matched one.
  const pal = palette || CODE_PALETTES[theme.palette.mode === 'dark' ? 'dark' : 'light'];
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    try { navigator.clipboard.writeText(text); } catch (_e) { /* clipboard blocked */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Tooltip title={copied ? 'Copied!' : title} arrow>
      <IconButton
        size="small"
        onClick={onCopy}
        sx={{ color: copied ? '#27c93f' : pal.muted, '&:hover': { color: pal.copyHover } }}
      >
        {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const CodeBlock = ({ label, code, fields, onFieldClick }) => {
  const theme = useTheme();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const palette = CODE_PALETTES[mode];
  const hl = HL_PALETTES[mode];
  return (
    <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${palette.border}` }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pl: 1.5, pr: 0.5, py: 0.5, bgcolor: palette.headerBg, borderBottom: `1px solid ${palette.border}` }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Stack direction="row" spacing={0.5}>
            {CODE_DOTS.map((c) => (
              <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c, opacity: 0.9 }} />
            ))}
          </Stack>
          <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 600, letterSpacing: 0.4 }}>
            {label}
          </Typography>
        </Stack>
        <CopyChip text={code} palette={palette} />
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          overflowX: 'auto',
          bgcolor: palette.bg,
          color: palette.text,
          fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: 12.5,
          lineHeight: 1.7,
          whiteSpace: 'pre',
        }}
      >
        {highlightCode(code, fields, onFieldClick, hl)}
      </Box>
    </Box>
  );
};

const SectionLabel = ({ children, sx }) => (
  <Typography
    variant="overline"
    sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 1, ...sx }}
  >
    {children}
  </Typography>
);

const DeploymentDetailPage = ({ 
  getDeploymentDetail, 
  clearState, 
  deployment, 
  isLoading, 
  error,
  deleteDeployment,
  deleteSuccess,
  getDeploymentUsage,
  clearUsageState,
  usageData,
  usageLoading,
  usageError,
  runSegmentInference,
  clearInferenceState,
  segmentInferenceFailure,
  inferenceData,
  inferenceLoading,
  inferenceError,
  inferencePendingId,
  checkPendingInference,
  clearInferenceCheck,
  inferenceCheckResponse,
  getSegmentDetail,
  clearSegmentState,
  segmentData,
  segmentLoading,
  segmentError,
  fetchModelDetail,
  modelData,
  modelLoading,
  modelError,
  getWorkspaceSegments,
  workspaceSegments,
  workspaceSegmentsLoading,
  retryRequest,
  clearRetryState,
  retryLoading,
  retryResponse,
  retryError,
}) => {
  const { promotionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const nameOf = useDisplayNames();
  // Measurement units per signal key (forecast labels are signal keys).
  const unitOf = useSignalUnits();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modelDetailsModalOpen, setModelDetailsModalOpen] = useState(false);
  const [inferSegmentDialogOpen, setInferSegmentDialogOpen] = useState(false);
  const [inferenceResult, setInferenceResult] = useState(null);
  const [isInferring, setIsInferring] = useState(false);
  // Deep-link support: ?tab=api opens the API Guide tab directly (e.g. from
  // the goal Serving section's "View API guide" link).
  const tabFromQuery = (search) => (new URLSearchParams(search).get('tab') === 'api' ? 1 : 0);
  const [tabValue, setTabValue] = useState(() => tabFromQuery(location.search));
  const [timePeriod, setTimePeriod] = useState('24h');
  const [binSize, setBinSize] = useState('hour');
  const [apiLang, setApiLang] = useState('curl');
  const [apiMode, setApiMode] = useState('segment');
  // Endpoint addressing: 'stable' targets /inference/pipelines/<id>/…
  // which always resolves to the pipeline's CURRENT promoted deployment
  // (integrations survive promotions); 'pinned' targets this exact
  // deployment id forever. Stable is the right default for anything a
  // user wires into their own systems.
  const [urlMode, setUrlMode] = useState('stable');
  const [selectedField, setSelectedField] = useState(null);
  // Phase 13.O: foundation-direct deploys need a user-picked segment
  // to anchor inference (the deployment itself has no segment).
  const [foundationSegmentId, setFoundationSegmentId] = useState('');
  const [foundationHorizon, setFoundationHorizon] = useState('');

  useEffect(() => {
    if (promotionId) {
      getDeploymentDetail({ promotion_id: promotionId });
    }

    return () => {
      clearState();
      clearUsageState();
      clearSegmentState();
      clearInferenceCheck();
    };
  }, [getDeploymentDetail, clearState, clearUsageState, clearSegmentState, clearInferenceCheck, promotionId]);

  // Keep the active tab in sync if the ?tab query param changes while mounted.
  useEffect(() => {
    setTabValue(tabFromQuery(location.search));
  }, [location.search]);

  // Fetch usage data when deployment data is available or time period/bin size changes
  useEffect(() => {
    if (deployment && deployment.promotion_id) {
      // Fetch usage analytics using deployment_id (promotion_id)
      getDeploymentUsage({
        deployment_id: deployment.promotion_id,
        period: timePeriod,
        bin_size: binSize
      });
    }
  }, [deployment, getDeploymentUsage, timePeriod, binSize]);

  // Fetch model and segment data when modal is opened
  useEffect(() => {
    if (inferSegmentDialogOpen && deployment && deployment.model_id && !modelData) {
      fetchModelDetail(deployment.model_id);
    }
  }, [inferSegmentDialogOpen, deployment, fetchModelDetail, modelData]);

  // Fetch segment data when model data is available and modal is open
  useEffect(() => {
    if (inferSegmentDialogOpen && modelData && (modelData.segment || modelData.segment_id) && !segmentData) {
      const segmentId = modelData.segment || modelData.segment_id;
      getSegmentDetail(segmentId);
    }
  }, [inferSegmentDialogOpen, modelData, getSegmentDetail, segmentData]);

  // Phase 13.O: when the dialog opens for a foundation-direct deploy,
  // pull the workspace's segments so the user can pick one as the
  // forecast anchor. The X-Workspace-Id header is set globally elsewhere.
  useEffect(() => {
    if (inferSegmentDialogOpen && deployment?.is_foundation_direct) {
      getWorkspaceSegments();
    }
  }, [inferSegmentDialogOpen, deployment, getWorkspaceSegments]);

  // Handle successful inference.
  //
  // Previously this opened the modal so the user could read the
  // prediction payload inline. The detail page at /inferences/<uid>
  // is the canonical place for that view (forecasts, debug payload,
  // history charts, etc.), so once a fresh inference returns we
  // hand the user off to it instead of duplicating the surface in
  // the modal.
  //
  // The `isInferring` flag guards against a regression where stale
  // `inferenceData` left over in Redux from a previous visit would
  // immediately navigate the user away on mount. We only navigate
  // for a result that came from a click in *this* dialog session.
  useEffect(() => {
    if (!inferenceData || !isInferring) return;
    const inferenceId = inferenceData.inference_id;
    setIsInferring(false);
    setInferSegmentDialogOpen(false);
    clearInferenceState();
    if (inferenceId) {
      navigate(`/inferences/${inferenceId}`);
    }
    // No inference_id (older response shape or partial failure) -
    // fall through silently. The user is back on the deployment
    // page; they can hit Run Inference again.
  }, [inferenceData, isInferring, clearInferenceState, navigate]);

  // If the inference fails, drop the "we owe a navigation" flag so
  // the user can retry without the next success silently
  // navigating mid-edit.
  useEffect(() => {
    if (inferenceError) setIsInferring(false);
  }, [inferenceError]);

  // Handle successful deletion
  useEffect(() => {
    if (deleteSuccess) {
      setTimeout(() => {
        navigate('/deployments');
      }, 1000);
    }
  }, [deleteSuccess, navigate]);

  const handleBack = () => {
    navigate('/deployments');
  };

  const handleNavigateToModel = () => {
    if (deployment?.training_id) {
      setModelDetailsModalOpen(true);
    }
  };

  // Whether the current deployment is in a terminal failed state and
  // should expose a "Retry Deployment" button instead of "Run Inference".
  // Mirrors resolve_effective_status's terminal precedence so the FE
  // logic stays consistent with the BE-computed status.
  const isFailedDeployment = (() => {
    const TERMINAL = new Set(['failed', 'inactive', 'archived']);
    const dbStatus = String(deployment?.status || '').toLowerCase();
    return TERMINAL.has(dbStatus);
  })();

  // Retry deployment IN PLACE: re-runs the same background deployment
  // flow on the existing promotion record. Same uid, same URL, status
  // flips failed -> initializing -> deploying -> deployed (or back to
  // failed). User stays on this page; we just keep refreshing the
  // detail to surface the new status.
  const handleRetryDeployment = () => {
    if (!deployment?.promotion_id) return;
    retryRequest({ promotionId: deployment.promotion_id });
  };

  // After a successful retry kick-off, refresh THIS detail page once (no
  // navigation -- the promotion_id didn't change). The deploying->deployed
  // transition then arrives live via the realtime hook below.
  useEffect(() => {
    if (!retryResponse) return;
    clearRetryState();
    if (promotionId) {
      getDeploymentDetail({ promotion_id: promotionId });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryResponse]);

  // Live (no polling): re-fetch this deployment when the backend reports a
  // deployment status transition (deploying -> deployed/ready/failed). A
  // refetch is cheap + idempotent, so we don't gate on the id. Reconnect
  // reconciles too.
  useRealtimeRefresh(
    (msg) => typeof msg.event === 'string' && msg.event.indexOf('deployment.') === 0,
    () => { if (promotionId) getDeploymentDetail({ promotion_id: promotionId }); },
  );

  // Async "Run Inference" (foundation-direct): the POST returned 202 while the
  // GPU server warmed, so the actual forecast lands here over the WebSocket
  // ('inference.completed'). On the matching event we resolve the in-progress
  // dialog: navigate to the result on success, or surface the error. On a
  // socket RE-connect (msg === null) we reconcile the pending id by fetching
  // it directly, in case the event was emitted while we were briefly offline
  // (the record exists only once the inference succeeded).
  useRealtimeRefresh(
    (msg) => msg && msg.event === 'inference.completed',
    (msg) => {
      if (!isInferring) return;
      const pid = inferencePendingId;

      if (msg) {
        const d = msg.data || {};
        const matches = (pid && d.inference_id === pid)
          || (deployment && d.deployment_id === deployment.promotion_id);
        if (!matches) return;
        if (d.success && (d.inference_id || pid)) {
          setIsInferring(false);
          setInferSegmentDialogOpen(false);
          clearInferenceState();
          navigate(`/inferences/${d.inference_id || pid}`);
        } else {
          setIsInferring(false);
          segmentInferenceFailure({
            code: 'INFERENCE_FAILED',
            message: d.error || 'Inference failed',
          });
        }
        return;
      }

      // Reconnect reconciliation. The saga fetches /inference/<pid>;
      // failures stay silent (still pending / no record yet — keep
      // waiting; the safety timeout below bounds the spinner either way)
      // and a success lands in the effect right below.
      if (pid) {
        checkPendingInference({ inferenceId: pid });
      }
    },
  );

  // A reconnect reconciliation fetch found the inference record — the
  // run succeeded while we were offline, so resolve the dialog exactly
  // like the WS event path.
  useEffect(() => {
    if (!inferenceCheckResponse || !isInferring) return;
    const pid = inferenceCheckResponse.inference_id;
    if (!pid || pid !== inferencePendingId || !inferenceCheckResponse.data) return;
    setIsInferring(false);
    setInferSegmentDialogOpen(false);
    clearInferenceState();
    clearInferenceCheck();
    navigate(`/inferences/${pid}`);
  }, [
    inferenceCheckResponse, isInferring, inferencePendingId,
    clearInferenceState, clearInferenceCheck, navigate,
  ]);

  // Safety net for the async path: if neither the WS event nor a reconnect
  // reconciliation resolves a pending inference within the worst-case cold
  // start window, stop spinning and tell the user where to look rather than
  // hang forever.
  useEffect(() => {
    if (!isInferring || !inferencePendingId) return undefined;
    const timer = setTimeout(() => {
      setIsInferring(false);
      segmentInferenceFailure({
        code: 'TIMEOUT',
        message: (
          'This is taking longer than usual. It’s still running in the '
          + 'background — your forecast will appear on the Inferences page shortly.'
        ),
      });
    }, 12 * 60 * 1000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInferring, inferencePendingId]);

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deployment) {
      setIsDeleting(true);
      deleteDeployment({ promotion_id: deployment.promotion_id });
      setDeleteDialogOpen(false);
    }
  };

  const handleInferSegment = (featureDebug = false) => {
    if (deployment && deployment.promotion_id) {
      const isFoundation = !!deployment.is_foundation_direct;
      // Marker for the inference-success effect: only navigate to
      // /inferences/<uid> when the next `inferenceData` was
      // produced by *this* click (not stale Redux state from a
      // previous visit).
      setIsInferring(true);
      runSegmentInference({
        deployment_id: deployment.promotion_id,
        feature_debug: featureDebug,
        // Foundation-direct extras (ignored by the trained-model path).
        ...(isFoundation && foundationSegmentId
          ? { segment_id: foundationSegmentId }
          : {}),
        ...(isFoundation && foundationHorizon
          ? { horizon: parseInt(foundationHorizon, 10) }
          : {})
      });
    }
  };

  // Translate the wire-format ``deployment_type`` to a user-friendly
  // label. The previous binary mapping (shared -> "Shared",
  // anything-else -> "Dedicated") hid the fact that ``foundation``
  // deployments aren't on KServe at all - they run on the
  // centralised foundation server pool.
  const formatDeploymentType = (t) => {
    switch (t) {
      case 'shared':
      case 'modelmesh_shared':
        return 'Shared';
      case 'dedicated':
      case 'kserve_dedicated':
        return 'Dedicated';
      case 'foundation':
        return 'Foundation · shared';
      case 'external_fm':
        return 'External FM · shared';
      default:
        return t || '—';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'PPpp');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Status color follows the same precedence as getDisplayStatus:
  // terminal DB states (failed/inactive/archived) win over cluster
  // state, then live cluster state, then DB fallback. Keeps the dot
  // color in sync with the label text -- no more "green dot + Failed
  // text" mismatches.
  const getStatusColor = (deployment) => {
    const TERMINAL = new Set(['failed', 'inactive', 'archived']);
    const dbStatus = String(deployment.status || '').toLowerCase();
    if (TERMINAL.has(dbStatus)) {
      return dbStatus === 'failed' ? '#d32f2f' : '#9e9e9e';
    }
    if (deployment.ready) return '#2f7d31'; // Green
    const inflight = ['deploying', 'initializing', 'starting'];
    if (inflight.includes(deployment.live_status) || inflight.includes(deployment.status)) {
      return '#ed6c02'; // Orange
    }
    if (deployment.live_status === 'failed') return '#d32f2f'; // Red (cluster-only fail)
    return '#9e9e9e'; // Gray
  };

  // Display status precedence (mirrors resolve_effective_status on the
  // BE for safety in case an older API response hasn't applied the
  // terminal-state rule yet):
  //   1. Terminal Mongo statuses (failed/inactive/archived) ALWAYS win
  //      -- a failed promotion stays "Failed" regardless of what the
  //      cluster currently reports (the IS may be missing because it
  //      was cleaned up, or "initializing" because Triton is still
  //      backoff-retrying a load that will never succeed).
  //   2. Live cluster status when it's informative (not unknown/not_found).
  //   3. Mongo status as fallback.
  const getDisplayStatus = (deployment) => {
    const TERMINAL = new Set(['failed', 'inactive', 'archived']);
    const dbStatus = deployment.status;
    if (dbStatus && TERMINAL.has(String(dbStatus).toLowerCase())) {
      return dbStatus.charAt(0).toUpperCase() + dbStatus.slice(1);
    }
    const live = deployment.live_status;
    if (live && live !== 'unknown' && live !== 'not_found') {
      return live.charAt(0).toUpperCase() + live.slice(1);
    }
    if (dbStatus) {
      return dbStatus.charAt(0).toUpperCase() + dbStatus.slice(1);
    }
    return 'Unknown';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    // Handle error object structure properly
    const errorMessage = typeof error === 'object' ? 
      (error.message || error.error?.message || JSON.stringify(error)) : 
      error;
    
    const errorCode = typeof error === 'object' ? 
      (error.code || error.error?.code) : 
      null;

    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <ErrorIcon color="error" style={{ fontSize: 60, marginBottom: 16 }} />
        <Typography variant="h5" color="error" gutterBottom>Error Loading Deployment Details</Typography>
        <Typography variant="body1" paragraph>{errorMessage}</Typography>
        {errorCode && (
          <Typography variant="body2" color="text.secondary" paragraph>
            Error Code: {errorCode}
          </Typography>
        )}
        <Alert severity="warning" sx={{ mb: 3, maxWidth: 500, margin: '0 auto' }}>
          {errorCode === 'FORBIDDEN' ? 
            'You do not have permission to access this deployment.' :
            'This could be due to a network issue, authorization problem, or the deployment may not exist.'
          }
        </Alert>
        <CMButtonOutlined
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Deployments
        </CMButtonOutlined>
      </Box>
    );
  }

  if (!deployment) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>Deployment not found</Typography>
        <CMButtonOutlined
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Deployments
        </CMButtonOutlined>
      </Box>
    );
  }

  const statusColor = getStatusColor(deployment);

  // ── API guide content ──────────────────────────────────────────────
  // Two ways to call a deployment, surfaced as a mode toggle:
  //   • "segment"  → POST /infer-segment — pass a segment_id and Predict
  //                  assembles the recent context for you (trained models
  //                  resolve their own segment automatically, no body).
  //   • "raw"      → POST /infer — you send the numeric context window.
  // Snippets are pre-filled with the real endpoint + workspace id so the
  // only thing a user swaps in is their token.
  //
  // Two addressing modes: the STABLE url routes by pipeline id and follows
  // promotions automatically (the backend resolves the current deployment
  // per request); the PINNED url targets this deployment forever.
  // Foundation-direct deployments have no pipeline row, so they only get
  // the pinned form. (deployment.model_id IS the pipeline id — pipelines
  // live in the legacy-named `models` collection.)
  const pinnedBase = `${config.API_URL}/inference/deployments/${deployment.promotion_id || 'DEPLOYMENT_ID'}`;
  const stableBase = deployment.model_id
    ? `${config.API_URL}/inference/pipelines/${deployment.model_id}`
    : null;
  const useStableUrl = urlMode === 'stable' && Boolean(stableBase);
  const baseInferUrl = useStableUrl ? stableBase : pinnedBase;
  const rawUrl = `${baseInferUrl}/infer`;
  const segmentUrl = `${baseInferUrl}/infer-segment`;
  const wsId = (typeof window !== 'undefined' && localStorage.getItem('WORKSPACE_ID')) || 'YOUR_WORKSPACE_ID';
  const sampleInput = '[[1.0, 2.0, 3.0, 4.0, 5.0, 6.0, 7.0, 8.0, 9.0, 10.0]]';
  const sampleSegmentId = 'seg_8f3c09e0';
  const isFoundation = Boolean(deployment.is_foundation_direct)
    || deployment.deployment_type === 'foundation';
  const isPredictfmFamily = (deployment.foundation_family || 'predictfm') === 'predictfm';
  const sampleHorizon = deployment.horizon
    || (isFoundation && isPredictfmFamily ? 128 : 64);
  const isSegmentMode = apiMode === 'segment';
  const inferenceUrl = isSegmentMode ? segmentUrl : rawUrl;

  // Knobs that only foundation models honour per-request.
  const foundationKnobs = isFoundation
    ? [
      { key: 'horizon', val: String(sampleHorizon) },
      { key: 'samples', val: '100' },
      ...(isFoundation && !isPredictfmFamily
        ? [{ key: 'quantile_levels', val: '[0.1, 0.5, 0.9]' }] : []),
    ]
    : [];

  // Body entries for the active mode. Trained models in segment mode need
  // no body at all — they resolve their own segment + live data.
  const bodyEntries = isSegmentMode
    ? (isFoundation
      ? [{ key: 'segment_id', val: `"${sampleSegmentId}"` }, ...foundationKnobs]
      : [])
    : [{ key: 'input', val: sampleInput }, ...foundationKnobs];
  const noBody = bodyEntries.length === 0;

  const jsonBody = noBody
    ? '{}'
    : `{\n${bodyEntries.map((e) => `  "${e.key}": ${e.val}`).join(',\n')}\n}`;
  const jsonBodyInline = noBody
    ? '{}'
    : `{${bodyEntries.map((e) => `"${e.key}": ${e.val}`).join(', ')}}`;
  const jsBodyObj = noBody
    ? '{}'
    : `{ ${bodyEntries.map((e) => `${e.key}: ${e.val}`).join(', ')} }`;

  const apiSnippets = {
    curl: `curl -X POST '${inferenceUrl}' \\
  -H 'Authorization: Bearer YOUR_API_TOKEN' \\
  -H 'Content-Type: application/json' \\
  -H 'X-Workspace-ID: ${wsId}' \\
  -d '${jsonBody}'`,
    python: `import requests

url = "${inferenceUrl}"
headers = {
    "Authorization": "Bearer YOUR_API_TOKEN",
    "X-Workspace-ID": "${wsId}",
}
payload = ${jsonBodyInline}

resp = requests.post(url, headers=headers, json=payload)
resp.raise_for_status()

result = resp.json()
print(result["predictions"])`,
    javascript: `const res = await fetch("${inferenceUrl}", {
  method: "POST",
  headers: {
    "Authorization": "Bearer YOUR_API_TOKEN",
    "Content-Type": "application/json",
    "X-Workspace-ID": "${wsId}",
  },
  body: JSON.stringify(${jsBodyObj}),
});

const data = await res.json();
console.log(data.predictions);`,
  };
  const responseSample = `{
  "predictions": [
    { "label": "output", "point": [12.4, 13.1, 13.8] }
  ],
  "horizon": ${isFoundation ? sampleHorizon : 3},
  "labels": ["output"],${isSegmentMode ? `\n  "segment_id": "${sampleSegmentId}",` : ''}
  "deployment_id": "${deployment.promotion_id || 'DEPLOYMENT_ID'}",
  "inference_id": "8f3c09e0-0aa6-4b03-8b9c-4cab9eb418ef",
  "latency_ms": 245.67,
  "timestamp": "2026-06-28T19:14:00.000Z"
}`;
  const apiLangLabel = (API_LANGS.find((l) => l.id === apiLang) || API_LANGS[0]).label;

  // Clickable field reference. Each row opens the right-side drawer with the
  // full description + a colourful example. We only expose the fields that
  // actually appear in the active snippet so the highlights line up.
  const headerFields = [
    {
      section: 'Header', name: 'Authorization', type: 'string', required: true,
      desc: 'Bearer token used to authenticate every request. Generate one from your account settings and send it as "Bearer <token>". Keep it secret — it carries your account\u2019s permissions.',
      example: 'Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6...',
    },
    {
      section: 'Header', name: 'X-Workspace-ID', type: 'string', required: true,
      desc: 'Identifies the workspace that owns this deployment. Requests are scoped — and billed — to this workspace, so it must match the deployment\u2019s workspace.',
      example: `X-Workspace-ID: ${wsId}`,
    },
    {
      section: 'Header', name: 'Content-Type', type: 'string', required: false,
      desc: 'Set to application/json whenever you send a JSON request body (every example here does).',
      example: 'Content-Type: application/json',
    },
  ];
  const FIELD_DEFS = {
    input: {
      section: 'Body', name: 'input', type: 'number[][] | number[]', required: true,
      desc: 'One or more context windows — the recent history the model extrapolates from. Pass a 2D array [series][steps] to forecast several series in one call, or a 1D array for a single one.',
      example: `"input": ${sampleInput}`,
    },
    segment_id: {
      section: 'Body', name: 'segment_id', type: 'string', required: true,
      desc: 'The saved segment to forecast. Predict pulls the recent window of that segment\u2019s signals, prepares the model input for you, and maps the forecast back onto the segment\u2019s labels — so you never assemble the raw array by hand. Find segment ids in the Studio / segments list.',
      example: `"segment_id": "${sampleSegmentId}"`,
    },
    horizon: {
      section: 'Body', name: 'horizon', type: 'integer', required: false,
      desc: `Number of future steps to forecast. Foundation models honour this on every request; if you omit it the deployment falls back to its configured horizon (${sampleHorizon}).`,
      example: `"horizon": ${sampleHorizon}`,
    },
    samples: {
      section: 'Body', name: 'samples', type: 'integer', required: false,
      desc: 'How many probabilistic sample paths to draw before reducing to the quantile bands. Higher values give smoother intervals at a small latency cost (default 100).',
      example: '"samples": 100',
    },
    quantile_levels: {
      section: 'Body', name: 'quantile_levels', type: 'number[]', required: false,
      desc: 'The quantiles returned alongside the median forecast, used to draw the uncertainty band.',
      example: '"quantile_levels": [0.1, 0.5, 0.9]',
    },
  };
  const bodyFields = bodyEntries.map((e) => FIELD_DEFS[e.key]).filter(Boolean);
  const apiFields = [...headerFields, ...bodyFields];

  return (
    <Box sx={{ p: 0 }}>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Deployment</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this deployment? 
            This action will remove the model from active serving and cannot be undone.
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Model: {deployment.model_name || deployment.model_id}
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 3 }}>
          <CMButtonOutlined
            startIcon={<CancelIcon />}
            onClick={() => setDeleteDialogOpen(false)}
          >
            Cancel
          </CMButtonOutlined>
          <CMButtonError
            startIcon={isDeleting
              ? <CircularProgress size={14} color="inherit" />
              : <DeleteIcon />}
            onClick={confirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </CMButtonError>
        </DialogActions>
      </Dialog>

      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* Breadcrumb Navigation */}
        <PageBreadcrumbs 
          items={[
            {
              text: "Deployments",
              icon: <DeploymentsIcon />,
              link: "/deployments"
            },
            {
              text: deployment.model_name || deployment.model_id
            }
          ]}
          sx={{ mb: 2 }}
        />

        {/* Action Buttons Row */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex' }}>
            {/* Run Inference Button */}
            {/* Failed deployments can't serve inference, so swap the
                primary CTA. "Retry Deployment" re-promotes the same
                training (creates a fresh promotion record) instead
                of leaving the user stuck on a button that can't work. */}
            {isFailedDeployment ? (
              <CMButton
                startIcon={retryLoading
                  ? <CircularProgress size={14} color="inherit" />
                  : <ReplayIcon />}
                onClick={handleRetryDeployment}
                disabled={retryLoading || !deployment?.training_id}
                sx={{ mr: 2 }}
              >
                {retryLoading ? 'Retrying…' : 'Retry Deployment'}
              </CMButton>
            ) : (
              <CMButton
                startIcon={<LaunchIcon />}
                onClick={() => setInferSegmentDialogOpen(true)}
                disabled={!deployment?.promotion_id}
                sx={{ mr: 2 }}
              >
                Run Inference
              </CMButton>
            )}

            {/* View Model Details Button */}
            <CMButtonOutlined
              startIcon={<InfoIcon />}
              onClick={handleNavigateToModel}
              sx={{ mr: 2 }}
              disabled={!deployment?.training_id}
            >
              Model Details
            </CMButtonOutlined>

            {/* Delete Button */}
            <CMButtonError
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </CMButtonError>
          </Box>
        </Box>

        {/* Success message for deletion */}
        {deleteSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Deployment deleted successfully. Redirecting to deployments list...
          </Alert>
        )}

        {/* Retry feedback. The post-success effect navigates away,
            so this Alert really only renders during the in-flight
            window or when retry itself failed (e.g. permission
            denied, training row deleted). */}
        {retryError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => clearRetryState()}>
            Retry failed: {typeof retryError === 'object'
              ? (retryError.message || JSON.stringify(retryError))
              : String(retryError)}
          </Alert>
        )}
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              fontSize: '12px',
              minHeight: '36px',
              padding: '6px 12px',
              '& .MuiSvgIcon-root': {
                fontSize: '16px'
              }
            }
          }}
        >
          <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
          <Tab label="API Guide" icon={<CodeIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
          {/* Overview Card */}
          <Surface sx={{ mb: 3, p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DeploymentsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="div">
                  Deployment Overview
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Model Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    {deployment.model_name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                    <StatusChip
                      status={getDisplayStatus(deployment)}
                      tone={statusColor === '#2f7d31' ? 'success'
                        : statusColor === '#ed6c02' ? 'warning'
                        : statusColor === '#d32f2f' ? 'error'
                        : 'neutral'}
                      label={getDisplayStatus(deployment)}
                      size="md"
                    />
                    {/* Internal "Live: …" / "DB: …" chips removed -
                        users don't care which subsystem said what.
                        getDisplayStatus already merges them with the
                        right precedence (terminal DB states win) so
                        the single label above is always the canonical
                        answer. Full state breakdown still available
                        in the conditions table further down for
                        debugging. */}
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Deployment Type</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDeploymentType(deployment.deployment_type)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Inference Count</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {deployment.inference_count || 0}
                  </Typography>
                </Grid>
              </Grid>
          </Surface>

            {/* Usage Analytics Card */}
          <Surface sx={{ mb: 3, p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BarChartIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="div">
                      Usage Analytics
                    </Typography>
                    {usageLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
                  </Box>
                  
                  {/* Time Controls */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Time Period Selector */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="time-period-label">Period</InputLabel>
                      <Select
                        labelId="time-period-label"
                        value={timePeriod}
                        label="Period"
                        onChange={(e) => setTimePeriod(e.target.value)}
                      >
                        <MenuItem value="24h">Last 24 Hours</MenuItem>
                        <MenuItem value="7d">Last 7 Days</MenuItem>
                        <MenuItem value="30d">Last 30 Days</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Bin Size Selector */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="bin-size-label">Granularity</InputLabel>
                      <Select
                        labelId="bin-size-label"
                        value={binSize}
                        label="Granularity"
                        onChange={(e) => setBinSize(e.target.value)}
                      >
                        <MenuItem value="hour">Hourly</MenuItem>
                        <MenuItem value="day">Daily</MenuItem>
                        <MenuItem value="week">Weekly</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                
                {usageError ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Unable to fetch usage data: {typeof usageError === 'object' ? 
                      (usageError.message || usageError.error?.message || 'Unknown error') : 
                      usageError}
                  </Alert>
                ) : (
                  <Box>
                    {/* Usage Chart */}
                    <Paper sx={{ p: 2, height: 180, mb: 3 }}>
                      {usageLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (() => {
                        const accent = accentFor(theme.palette.mode);
                        // Defensive: if the backend returns a malformed
                        // timestamp (we hit a "+00:00Z" bug once), fall back
                        // to the raw string instead of calling format() on an
                        // Invalid Date and crashing the whole page.
                        // On a point scale the x-label IS the category key, so
                        // it must be unique. Hourly buckets over a multi-day
                        // period need the date too — otherwise every day's
                        // "HH:mm" collapses onto the same 24 x-slots and the
                        // line folds back over itself.
                        const fmt = binSize !== 'hour'
                          ? 'MMM dd'
                          : (timePeriod === '24h' ? 'HH:mm' : 'MMM d, HH:mm');
                        const chartData = usageData?.usage_over_time
                          ? usageData.usage_over_time.map((point) => {
                            const d = new Date(point.timestamp);
                            const x = isNaN(d.getTime())
                              ? String(point.timestamp ?? '')
                              : format(d, fmt);
                            return { x, y: point.requests || 0 };
                          })
                          : Array.from(
                            { length: timePeriod === '24h' ? 24 : timePeriod === '7d' ? 7 : 30 },
                            (_, i) => {
                              const date = new Date();
                              if (timePeriod === '24h') {
                                date.setHours(date.getHours() - (23 - i));
                                return { x: format(date, 'HH:mm'), y: 0 };
                              }
                              date.setDate(date.getDate() - ((timePeriod === '7d' ? 6 : 29) - i));
                              return { x: format(date, 'MMM dd'), y: 0 };
                            },
                          );
                        // Thin the x ticks so a 24/30-point series doesn't
                        // render a wall of overlapping rotated labels.
                        const step = Math.max(1, Math.ceil(chartData.length / 6));
                        const tickValues = chartData
                          .filter((_, i) => i % step === 0)
                          .map((d) => d.x);
                        return (
                          <ResponsiveLine
                            data={[{ id: 'requests', data: chartData }]}
                            margin={{ top: 16, right: 24, bottom: 36, left: 48 }}
                            xScale={{ type: 'point' }}
                            yScale={{ type: 'linear', min: 0, max: 'auto' }}
                            curve="monotoneX"
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                              tickSize: 0,
                              tickPadding: 10,
                              tickRotation: 0,
                              tickValues,
                            }}
                            axisLeft={{
                              tickSize: 0,
                              tickPadding: 8,
                              tickValues: 4,
                              format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v),
                            }}
                            colors={[accent]}
                            lineWidth={2}
                            enablePoints={false}
                            enableArea
                            areaOpacity={1}
                            enableGridX={false}
                            enableGridY
                            gridYValues={4}
                            useMesh
                            animate
                            motionConfig="gentle"
                            defs={[{
                              id: 'usageGradient',
                              type: 'linearGradient',
                              colors: [
                                { offset: 0, color: accent, opacity: 0.3 },
                                { offset: 100, color: accent, opacity: 0 },
                              ],
                            }]}
                            fill={[{ match: '*', id: 'usageGradient' }]}
                            theme={{
                              background: 'transparent',
                              textColor: theme.palette.text.secondary,
                              fontSize: 11,
                              axis: {
                                domain: { line: { stroke: 'transparent' } },
                                ticks: {
                                  line: { stroke: 'transparent' },
                                  text: { fill: theme.palette.text.secondary, fontSize: 11 },
                                },
                              },
                              grid: {
                                line: {
                                  stroke: theme.palette.divider,
                                  strokeWidth: 1,
                                  strokeOpacity: theme.palette.mode === 'dark' ? 0.25 : 0.5,
                                },
                              },
                              crosshair: { line: { stroke: accent, strokeWidth: 1, strokeOpacity: 0.5 } },
                            }}
                            tooltip={({ point }) => (
                              <Box sx={{
                                bgcolor: 'background.paper',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                px: 1.5,
                                py: 1,
                                boxShadow: theme.shadows[4],
                              }}>
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                  {point.data.x}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                                  {Number(point.data.y).toLocaleString()} requests
                                </Typography>
                              </Box>
                            )}
                          />
                        );
                      })()}
                    </Paper>

                  </Box>
                )}
          </Surface>

            {/* Identifiers & Timing Card */}
            <Surface sx={{ mb: 3, p: 2.5 }}>
                <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                  Identifiers & Timing
                </Typography>
                
                <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Promotion ID</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {deployment.promotion_id}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Model ID</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {deployment.model_id}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Training ID</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {deployment.training_id || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Promoted At</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2">
                          {deployment.promoted_at ? formatDate(deployment.promoted_at) : 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Deployed At</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2">
                          {deployment.deployed_at ? formatDate(deployment.deployed_at) : 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2">
                          {deployment.updated_at ? formatDate(deployment.updated_at) : 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                </Box>
            </Surface>
      </TabPanel>

      {/* API Guide Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ maxWidth: 920 }}>
          {/* Header + one-click "try it" */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'flex-start' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Inference API</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 580, mt: 0.5 }}>
                Authenticate with a bearer token and your workspace id, POST a request, then
                read the <code>predictions</code> array from the response. Snippets below are
                pre-filled with the endpoint — just drop in your token.
              </Typography>
            </Box>
            <CMButton
              startIcon={<LaunchIcon />}
              onClick={() => setInferSegmentDialogOpen(true)}
              disabled={!deployment?.promotion_id}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Try it now
            </CMButton>
          </Stack>

          {/* Call mode — segment-based (recommended) vs raw input */}
          <SectionLabel>How you call it</SectionLabel>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1, mb: 2 }}>
            {[
              {
                id: 'segment',
                title: 'From a segment',
                desc: 'Pass a segment_id — Predict pulls the recent data and prepares the input for you.',
              },
              {
                id: 'raw',
                title: 'Raw input',
                desc: 'Send the numeric context window yourself as an array.',
              },
            ].map((m) => {
              const selected = apiMode === m.id;
              return (
                <Box
                  key={m.id}
                  onClick={() => setApiMode(m.id)}
                  sx={{
                    flex: 1, cursor: 'pointer', borderRadius: 1.5, p: 1.5,
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                    transition: 'all .15s ease',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                    <Box sx={{
                      width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.title}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 2.75 }}>
                    {m.desc}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          {/* Endpoint addressing — stable (follows promotions) vs pinned */}
          {stableBase && (
            <>
              <SectionLabel>Endpoint addressing</SectionLabel>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1, mb: 2 }}>
                {[
                  {
                    id: 'stable',
                    title: 'Stable (recommended)',
                    desc: 'Addressed by pipeline id — always serves the pipeline\u2019s latest promoted version. Integrations keep working after every promotion.',
                  },
                  {
                    id: 'pinned',
                    title: 'Pinned to this version',
                    desc: 'Addressed by deployment id — keeps serving exactly this version, even after newer promotions.',
                  },
                ].map((m) => {
                  const selected = urlMode === m.id;
                  return (
                    <Box
                      key={m.id}
                      onClick={() => setUrlMode(m.id)}
                      sx={{
                        flex: 1, cursor: 'pointer', borderRadius: 1.5, p: 1.5,
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                        transition: 'all .15s ease',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                        <Box sx={{
                          width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                          border: '2px solid',
                          borderColor: selected ? 'primary.main' : 'divider',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.title}</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 2.75 }}>
                        {m.desc}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}

          {/* Endpoint */}
          <SectionLabel>Endpoint</SectionLabel>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              mt: 1, mb: 3, py: 0.75, pl: 1, pr: 0.5, borderRadius: 1.5,
              border: '1px solid', borderColor: 'divider',
              bgcolor: alpha(theme.palette.text.primary, 0.03),
            }}
          >
            <Chip
              label="POST"
              size="small"
              sx={{
                fontWeight: 700, borderRadius: 1, letterSpacing: 0.5,
                bgcolor: alpha(theme.palette.success.main, 0.15), color: 'success.main',
              }}
            />
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13, flex: 1, overflowX: 'auto', whiteSpace: 'nowrap' }}>
              {inferenceUrl}
            </Typography>
            <CopyChip text={inferenceUrl} title="Copy endpoint" />
          </Stack>

          {isSegmentMode && noBody && (
            <Alert severity="info" variant="outlined" sx={{ mb: 3, py: 0.5 }}>
              This trained model resolves its own segment and pulls the recent
              data automatically — no request body is required. Just POST to the
              endpoint with your auth headers.
            </Alert>
          )}
          {!isFoundation && !isSegmentMode && (
            <Alert severity="info" variant="outlined" sx={{ mb: 3, py: 0.5 }}>
              This is a trained model — it forecasts a fixed horizon set at
              training time, so <code>horizon</code> can&apos;t be overridden per
              request. Deploy a foundation model if you need a variable horizon.
            </Alert>
          )}

          {/* Request example — language picker + code */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <SectionLabel sx={{ mb: 0 }}>Request</SectionLabel>
            <Stack direction="row" spacing={0.75}>
              {API_LANGS.map((l) => {
                const selected = apiLang === l.id;
                return (
                  <Chip
                    key={l.id}
                    label={l.label}
                    size="small"
                    onClick={() => setApiLang(l.id)}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer', fontWeight: 600 }}
                  />
                );
              })}
            </Stack>
          </Stack>
          <CodeBlock
            label={apiLangLabel}
            code={apiSnippets[apiLang]}
            fields={apiFields}
            onFieldClick={(name) => {
              const f = apiFields.find((x) => x.name === name);
              if (f) setSelectedField(f);
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Tip: click any{' '}
            <Box component="span" sx={{ color: '#d2a8ff', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
              highlighted parameter
            </Box>{' '}
            above for its details.
          </Typography>

          {/* Response */}
          <SectionLabel sx={{ mt: 3, mb: 1 }}>Response · 200 OK</SectionLabel>
          <CodeBlock label="application/json" code={responseSample} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Each entry in <code>predictions</code> carries a <code>label</code> and its
            forecast <code>point</code> array; <code>horizon</code> is the number of steps
            returned. Errors come back as <code>{'{ "error": { "code", "message" } }'}</code>.
          </Typography>
        </Box>
      </TabPanel>

      {/* Field detail drawer — slides in from the right when a header or body
          field is clicked, showing its full description + a colourful example. */}
      <Drawer
        anchor="right"
        open={Boolean(selectedField)}
        onClose={() => setSelectedField(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, p: 3 } }}
      >
        {selectedField && (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}>
                {selectedField.section} field
              </Typography>
              <IconButton size="small" onClick={() => setSelectedField(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 20 }}>
                {selectedField.name}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                {selectedField.type}
              </Typography>
            </Stack>

            <Chip
              label={selectedField.required ? 'required' : 'optional'}
              size="small"
              variant="outlined"
              color={selectedField.required ? 'success' : 'default'}
              sx={{ height: 20, fontSize: 11, mb: 2.5 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.75 }}>
              {selectedField.desc}
            </Typography>

            <SectionLabel sx={{ mb: 1 }}>Example</SectionLabel>
            <CodeBlock label="example" code={selectedField.example} />
          </Box>
        )}
      </Drawer>

      {/* Segment Inference Dialog */}
      <Dialog
        open={inferSegmentDialogOpen}
        onClose={() => {
          setInferSegmentDialogOpen(false);
          clearInferenceState();
          // User dismissed the dialog before the result landed -
          // don't silently navigate them away if it arrives later.
          setIsInferring(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <SegmentsIcon sx={{ mr: 1 }} />
          Segment Inference
        </DialogTitle>
        <DialogContent>
          {inferenceLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Running inference...
              </Typography>
            </Box>
          ) : inferenceError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Inference Failed
              </Typography>
              <Typography variant="body2">
                {typeof inferenceError === 'object' ? inferenceError.message || 'Unknown error' : inferenceError}
              </Typography>
              {typeof inferenceError === 'object' && inferenceError.code && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Error Code: {inferenceError.code}
                </Typography>
              )}
            </Alert>
          ) : inferenceData ? (
            <Box>
              {/* Shock Playbook (discovery spec section 5.6): the forecast was
                  blended with the zero-shot generalist + event-study overlay
                  and intervals are widened — say so instead of pretending
                  precision. */}
              {inferenceData.shock && (
                <Alert
                  severity={inferenceData.shock.state === 'shock' ? 'warning' : 'info'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {inferenceData.shock.state === 'shock'
                      ? 'Shock mode — elevated uncertainty'
                      : 'Recovering from a shock'}
                  </Typography>
                  <Typography variant="body2">
                    {inferenceData.shock.note}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Blend: champion {Math.round((inferenceData.shock.blend?.champion || 0) * 100)}%
                    {inferenceData.shock.arms?.zero_shot
                      ? ` · zero-shot ${Math.round((inferenceData.shock.blend?.zero_shot || 0) * 100)}%`
                      : ''}
                    {inferenceData.shock.arms?.overlay
                      ? ` · event overlay ${Math.round((inferenceData.shock.blend?.overlay || 0) * 100)}%`
                      : ''}
                    {inferenceData.shock.stream ? ` · trigger: ${inferenceData.shock.stream}` : ''}
                  </Typography>
                  {/* The shock learning loop's payoff: per-step widened
                      intervals (conformal from graded past-shock forecasts
                      when the workspace has survived shocks before, else
                      event-study / volatility bands). */}
                  {(() => {
                    const entries = Object.entries(inferenceData.shock.intervals || {});
                    if (!entries.length) return null;
                    const [targetLabel, band] = entries[0];
                    const lower = Array.isArray(band?.lower) ? band.lower : [];
                    const upper = Array.isArray(band?.upper) ? band.upper : [];
                    if (!lower.length || lower.length !== upper.length) return null;
                    const fmt = (v) =>
                      typeof v === 'number'
                        ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : 'N/A';
                    const methodNote = {
                      shock_conformal: `calibrated on ${band.n_residuals || 0} graded forecasts from past shocks`,
                      event_study_band: 'from the measured event response',
                      volatility_fallback: 'from recent volatility',
                    }[band.method] || band.method;
                    return (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                          Widened uncertainty band for {targetLabel} ({methodNote})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {lower.map((lo, i) => (
                            <Chip
                              key={i}
                              label={`t+${i + 1}: ${fmt(lo)} – ${fmt(upper[i])}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })()}
                </Alert>
              )}
              {/* Phase 13.O: foundation-direct returns an array of
                  per-label forecasts ({label, point, quantiles}) -
                  render each as a sparkline w/ p10..p90 band. The
                  trained-model branch below preserves the legacy dict
                  rendering. */}
              {inferenceData.is_foundation_direct ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Forecasts ({(inferenceData.predictions || []).length} label{(inferenceData.predictions || []).length === 1 ? '' : 's'})
                  </Typography>
                  {(inferenceData.predictions || []).map((fc) => {
                    const point = fc.point || [];
                    const q10 = fc.quantiles?.['0.1'] || [];
                    const q50 = fc.quantiles?.['0.5'] || point;
                    const q90 = fc.quantiles?.['0.9'] || [];
                    const data = [
                      {
                        id: 'p50',
                        color: '#614dee',
                        data: q50.map((y, i) => ({ x: i + 1, y })),
                      },
                      {
                        id: 'p10',
                        color: '#aaa',
                        data: q10.map((y, i) => ({ x: i + 1, y })),
                      },
                      {
                        id: 'p90',
                        color: '#aaa',
                        data: q90.map((y, i) => ({ x: i + 1, y })),
                      },
                    ];
                    const last = point[point.length - 1];
                    const unit = unitOf(fc.label);
                    return (
                      <Surface
                        key={fc.label}
                        sx={{ p: 2, mb: 2 }}
                      >
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Label</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {fc.label}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Horizon</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {point.length} steps
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Final p50</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {typeof last === 'number'
                                ? `${last.toLocaleString(undefined, { maximumFractionDigits: 4 })}${unit ? ` ${unit}` : ''}`
                                : 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Box sx={{ height: 180 }}>
                          <ResponsiveLine
                            data={data}
                            margin={{ top: 10, right: 20, bottom: 30, left: 50 }}
                            xScale={{ type: 'linear' }}
                            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                            curve="monotoneX"
                            colors={(d) => d.color}
                            enablePoints={false}
                            enableArea={false}
                            useMesh
                            axisBottom={{ tickValues: 5 }}
                            axisLeft={{
                              tickValues: 4,
                              legend: unit || undefined,
                              legendOffset: unit ? -44 : 0,
                              legendPosition: 'middle',
                            }}
                            lineWidth={(d) => (d.id === 'p50' ? 2 : 1)}
                          />
                        </Box>
                      </Surface>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Predictions
                  </Typography>
                  <Surface sx={{ p: 2 }}>
                    {/* Trained-model inferences now flow through
                        canonicalize_predictions on the backend, so
                        `predictions` is Array<{label, point[, quantiles]}>
                        - same envelope the foundation branch above
                        consumes. The previous Object.entries() over an
                        array returned [["0", {label, point}], ...] and
                        attempted to render the {label, point} object as a
                        React child, which threw "Objects are not valid as
                        a React child (found: object with keys {label,
                        point})". Detect the shape explicitly and render
                        each prediction by its label + point value(s). The
                        legacy dict shape ({label_a: 0.5, ...}) is still
                        handled for any old cached/logged inferences. */}
                    {Array.isArray(inferenceData.predictions) ? (
                      <Grid container spacing={2}>
                        {inferenceData.predictions.map((fc, idx) => {
                          const point = Array.isArray(fc?.point) ? fc.point : [];
                          const isMulti = point.length > 1;
                          const single = point.length === 1 ? point[0] : null;
                          const label = fc?.label ?? `output_${idx}`;
                          const labelUnit = unitOf(fc?.label);
                          const fmt = (v) =>
                            (typeof v === 'number'
                              ? `${v.toLocaleString(undefined, { maximumFractionDigits: 4 })}${labelUnit ? ` ${labelUnit}` : ''}`
                              : 'N/A');
                          return (
                            <Grid item xs={12} sm={isMulti ? 12 : 6} md={isMulti ? 12 : 4} key={`${label}-${idx}`}>
                              <Typography variant="caption" color="text.secondary">
                                {String(label).replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                              </Typography>
                              {isMulti ? (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {point.length} step forecast
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {point.map((v, i) => (
                                      <Chip
                                        key={i}
                                        label={`t+${i + 1}: ${fmt(v)}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              ) : (
                                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                  {fmt(single)}
                                </Typography>
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : (
                      <Grid container spacing={2}>
                        {Object.entries(inferenceData.predictions || {}).map(([key, value]) => (
                          <Grid item xs={12} sm={6} md={4} key={key}>
                            <Typography variant="caption" color="text.secondary">
                              {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                            </Typography>
                            <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                              {typeof value === 'number'
                                ? value.toLocaleString()
                                : typeof value === 'string'
                                  ? value
                                  : JSON.stringify(value)}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Surface>
                </Box>
              )}

              {/* Metadata */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Inference Details
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Segment</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {inferenceData.segment_name || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Inference ID</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {inferenceData.inference_id || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Sequence Length</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {inferenceData.sequence_length || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', pl: 0, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Data Period</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pr: 0, py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {(() => {
                            // Defensive: the inference response may omit
                            // data_period entirely, return null fields, or
                            // hand back a malformed timestamp. Calling
                            // date-fns format() on Invalid Date throws
                            // RangeError and tears down the whole page,
                            // so guard each side independently.
                            const period = inferenceData.data_period;
                            if (!period || (!period.start && !period.end)) return 'N/A';
                            const safeFmt = (v) => {
                              if (!v) return null;
                              const d = new Date(v);
                              return isNaN(d.getTime()) ? null : format(d, 'MMM dd');
                            };
                            const start = safeFmt(period.start);
                            const end = safeFmt(period.end);
                            if (!start && !end) return 'N/A';
                            if (start && end) return `${start} - ${end}`;
                            return start || end;
                          })()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                </Box>
              </Box>

              {/* Feature Names */}
              {inferenceData.feature_names && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Features Used ({inferenceData.feature_names.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {inferenceData.feature_names.map((feature, index) => (
                      <Chip
                        key={index}
                        title={feature}
                        label={nameOf(feature)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              {/* Phase 13.O: foundation-direct deployments don't have an
                  associated segment. The model is segment-agnostic -
                  it accepts any time-series input. Surface that clearly
                  instead of the misleading "loading automatically"
                  placeholder. Segment-picker UI for foundation deploys
                  ships in a follow-up. */}
              {deployment?.is_foundation_direct ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Foundation model
                  </Typography>
                  <Surface sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Family / Slug
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {`${deployment.foundation_family || '—'} / ${deployment.foundation_slug || '—'}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Mode</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {deployment.foundation_adapter_id
                            ? 'Fine-tuned (LoRA adapter attached)'
                            : 'Zero-shot (no adapter)'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Surface>

                  {/* Segment picker - foundation deploys are
                      segment-agnostic, so the user picks which one to
                      forecast against. Backend pulls the trailing
                      window from ClickHouse and feeds the labels into
                      the backbone. */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                    Forecast Inputs
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="foundation-segment-label">
                          Segment to forecast
                        </InputLabel>
                        <Select
                          labelId="foundation-segment-label"
                          label="Segment to forecast"
                          value={foundationSegmentId}
                          onChange={(e) => setFoundationSegmentId(e.target.value)}
                          disabled={workspaceSegmentsLoading}
                        >
                          {(workspaceSegments || []).length === 0 && (
                            <MenuItem value="" disabled>
                              {workspaceSegmentsLoading
                                ? 'Loading segments...'
                                : 'No segments in this workspace'}
                            </MenuItem>
                          )}
                          {(workspaceSegments || []).map((seg) => (
                            <MenuItem key={seg.uid} value={seg.uid}>
                              {seg.name || seg.uid}
                              {seg.labels?.length
                                ? ` (${seg.labels.length} label${seg.labels.length > 1 ? 's' : ''})`
                                : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="foundation-horizon-label">
                          Horizon
                        </InputLabel>
                        <Select
                          labelId="foundation-horizon-label"
                          label="Horizon"
                          value={foundationHorizon || (deployment.horizon || 64).toString()}
                          onChange={(e) => setFoundationHorizon(e.target.value)}
                        >
                          {[16, 32, 64, 128, 256, 512].map((h) => (
                            <MenuItem key={h} value={h.toString()}>
                              {h} steps
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    The model consumes raw values from the segment's
                    declared labels (no scaler / feature engineering).
                    A trailing context window of ~4x the horizon is
                    pulled from the segment's recent data.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Segment Information
                  </Typography>
                  {segmentLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        Loading segment details...
                      </Typography>
                    </Box>
                  ) : segmentError ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Unable to fetch segment data: {typeof segmentError === 'object' ?
                        (segmentError.message || segmentError.error?.message || 'Unknown error') :
                        segmentError}
                    </Alert>
                  ) : segmentData ? (
                    <Surface sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Segment Name</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {segmentData.name || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Data Range</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {segmentData.data_summary?.time_range ?
                              `${Math.round(segmentData.data_summary.time_range.duration_seconds / 86400)} days` :
                              'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Surface>
                  ) : (
                    <Surface sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        Segment information will be loaded automatically
                      </Typography>
                    </Surface>
                  )}
                </Box>
              )}

              {/* Prediction Labels */}
              {segmentData && segmentData.fields?.labels && segmentData.fields.labels.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Prediction Labels ({segmentData.fields.labels.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {segmentData.fields.labels.map((label, index) => (
                      <Chip
                        key={index}
                        label={label.name}
                        size="small"
                        color="success"
                        variant="filled"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" paragraph>
                This will run intelligent inference that automatically prepares model input from live signal data 
                using the segment configuration shown above. The system will fetch live data, apply feature engineering, 
                and format the input exactly as used during training.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!inferenceData && !inferenceLoading && (
            <>
              {/* Foundation deploys: button is gated on segment
                  selection. Trained models: always allowed. Debug
                  mode is hidden for foundation deploys (no feature
                  vector to inspect). */}
              {!deployment?.is_foundation_direct && (
                <CMButtonOutlined
                  onClick={() => handleInferSegment(true)}
                  disabled={inferenceLoading}
                  startIcon={<CodeIcon />}
                >
                  Run with Debug
                </CMButtonOutlined>
              )}
              <CMButton
                onClick={() => handleInferSegment(false)}
                disabled={
                  inferenceLoading
                  || (deployment?.is_foundation_direct && !foundationSegmentId)
                }
                startIcon={inferenceLoading
                  ? <CircularProgress size={14} color="inherit" />
                  : <LaunchIcon />}
                title={
                  deployment?.is_foundation_direct && !foundationSegmentId
                    ? 'Pick a segment to forecast'
                    : ''
                }
              >
                {inferenceLoading ? 'Running…' : 'Run Inference'}
              </CMButton>
            </>
          )}
          <CMButtonOutlined
            onClick={() => {
              setInferSegmentDialogOpen(false);
              clearInferenceState();
              setIsInferring(false);
            }}
          >
            Close
          </CMButtonOutlined>
        </DialogActions>
      </Dialog>

      {/* Model Details Modal */}
      <TrainingDetailsModal
        open={modelDetailsModalOpen}
        onClose={() => setModelDetailsModalOpen(false)}
        trainingId={deployment?.training_id}
        formatDate={formatDate}
        hidePromoteButton={true}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  deployment: state.deployment_detail?.data,
  isLoading: state.deployment_detail?.loading || false,
  error: state.deployment_detail?.error,
  deleteSuccess: state.deployment_delete?.success || false,
  usageData: state.deployment_usage?.data,
  usageLoading: state.deployment_usage?.loading || false,
  usageError: state.deployment_usage?.error,
  inferenceData: state.segment_inference?.data,
  inferenceLoading: state.segment_inference?.loading || false,
  inferenceError: state.segment_inference?.error,
  inferencePendingId: state.segment_inference?.pendingId || null,
  inferenceCheckResponse: state.deployment_inference_check?.response || null,
  segmentData: state.segment_detail?.data,
  segmentLoading: state.segment_detail?.loading || false,
  segmentError: state.segment_detail?.error,
  modelData: state.model_detail?.response,
  modelLoading: state.model_detail?.isLoading || false,
  modelError: state.model_detail?.error,
  // Phase 13.O: workspace segments power the foundation segment picker.
  workspaceSegments: state.segments_list?.response?.segments || [],
  workspaceSegmentsLoading: state.segments_list?.isLoading || false,
  // In-place retry redux slice (promotion_retry). The detail page
  // surfaces a "Retry Deployment" button when the deployment is in
  // a terminal failed state -- click dispatches retryRequest which
  // POSTs to /deployments/<id>/retry and re-runs the
  // background deployment flow on the SAME promotion record.
  retryLoading: state.promotion_retry?.isLoading || false,
  retryResponse: state.promotion_retry?.response,
  retryError: state.promotion_retry?.error,
});

const mapDispatchToProps = {
  getDeploymentDetail,
  clearState,
  deleteDeployment,
  getDeploymentUsage,
  clearUsageState,
  runSegmentInference,
  clearInferenceState,
  segmentInferenceFailure,
  checkPendingInference: inferenceCheckRequest,
  clearInferenceCheck: inferenceCheckClearState,
  getSegmentDetail,
  clearSegmentState,
  fetchModelDetail,
  getWorkspaceSegments,
  retryRequest,
  clearRetryState,
};

export default connect(mapStateToProps, mapDispatchToProps)(DeploymentDetailPage);